<template>
  <v-container fluid>
    <v-card class="mb-4">
      <v-toolbar flat dark color="blue-grey darken-2">
        <v-toolbar-title class="white--text">Categories</v-toolbar-title>
        <v-spacer></v-spacer>

        <!-- Toggle Switch for Products -->
        <v-switch
            v-model="showProducts"
            :label="showProducts ? 'Hide Products': 'Show Products'"
            color="white"
            class="mt-0 mr-4"
            hide-details
            inset
        ></v-switch>

        <v-btn color="white" outlined @click="new_category(null)">
          <v-icon left>mdi-plus</v-icon>
          New Category
        </v-btn>
      </v-toolbar>

      <!-- Main Content with Loading State -->
      <template v-if="loading">
        <v-sheet class="pa-3">
          <v-progress-linear
              indeterminate
              color="blue-grey darken-2"
          ></v-progress-linear>
          <div class="mt-4" v-for="n in 3" :key="n">
            <v-sheet class="pa-3 mb-4">
              <v-row>
                <v-col cols="4">
                  <v-sheet
                      class="v-sheet--outlined"
                      height="40"
                      color="grey lighten-3"
                  ></v-sheet>
                </v-col>
                <v-col cols="8">
                  <v-sheet
                      class="v-sheet--outlined"
                      height="40"
                      color="grey lighten-3"
                  ></v-sheet>
                </v-col>
              </v-row>
            </v-sheet>
          </div>
        </v-sheet>
      </template>

      <template v-else>
        <v-simple-table>
          <template v-slot:default>
            <thead>
            <tr>
              <th class="text-left">Name</th>
              <th class="text-left">Sub Categories</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="category in categories" :key="category.id">
              <td style="width: 250px; vertical-align: top;">
                <div class="d-flex align-center">
                  <v-btn text class="pa-0 text-none" @click="edit_category(category)">
                    {{ category.name }}
                  </v-btn>
                  <v-btn icon small color="success" class="ml-2" @click="new_category(category.id)">
                    <v-icon small>mdi-plus</v-icon>
                  </v-btn>
                </div>

                <!-- Products for Root Level Category -->
                <div v-if="showProducts && category.products && category.products.length > 0" class="mt-3 ml-2">
                  <v-divider class="mb-2"></v-divider>
                  <div class="text-caption grey--text mb-2">
                    <v-icon x-small class="mr-1">mdi-package-variant</v-icon>
                    Products ({{ category.products.length }})
                  </div>
                  <div class="ml-2">
                    <v-chip
                        v-for="product in category.products"
                        :key="product.id"
                        small
                        outlined
                        color="primary"
                        class="ma-1"
                        @click="edit_product(product, category)"
                        style="cursor: pointer;"
                    >
                      <v-icon left x-small>mdi-package</v-icon>
                      {{ product.name }}
                    </v-chip>
                  </div>
                </div>
              </td>

              <td style="vertical-align: top;">
                <!-- First Level Children -->
                <div v-for="firstChild in category.children" :key="firstChild.id" class="mb-4">
                  <div class="d-flex align-center mb-2">
                    <v-icon small color="grey" class="mr-2">mdi-subdirectory-arrow-right</v-icon>
                    <v-btn text class="pa-0 text-none" @click="edit_category(firstChild)">
                      {{ firstChild.name }}
                    </v-btn>
                    <v-btn icon x-small color="success" class="ml-2" @click="new_category(firstChild.id)">
                      <v-icon x-small>mdi-plus</v-icon>
                    </v-btn>
                  </div>

                  <!-- Products for First Level -->
                  <div v-if="showProducts && firstChild.products && firstChild.products.length > 0" class="ml-6 mb-3">
                    <v-divider class="mb-2"></v-divider>
                    <div class="text-caption grey--text mb-2">
                      <v-icon x-small class="mr-1">mdi-package-variant</v-icon>
                      Products ({{ firstChild.products.length }})
                    </div>
                    <div class="ml-2">
                      <v-chip
                          v-for="product in firstChild.products"
                          :key="product.id"
                          small
                          outlined
                          color="primary"
                          class="ma-1"
                          @click="edit_product(product, firstChild)"
                          style="cursor: pointer;"
                      >
                        <v-icon left x-small>mdi-package</v-icon>
                        {{ product.name }}
                      </v-chip>
                    </div>
                  </div>

                  <!-- Second Level Children -->
                  <div class="ml-8">
                    <div v-for="secondChild in firstChild.children" :key="secondChild.id" class="mb-3">
                      <div class="d-flex align-center mb-2">
                        <v-icon small color="grey" class="mr-2">mdi-subdirectory-arrow-right</v-icon>
                        <v-btn text class="pa-0 text-none" @click="edit_category(secondChild)">
                          {{ secondChild.name }}
                        </v-btn>
                        <v-btn icon x-small color="success" class="ml-2" @click="new_category(secondChild.id)">
                          <v-icon x-small>mdi-plus</v-icon>
                        </v-btn>
                      </div>

                      <!-- Products for Second Level -->
                      <div v-if="showProducts && secondChild.products && secondChild.products.length > 0" class="ml-6 mb-3">
                        <v-divider class="mb-2"></v-divider>
                        <div class="text-caption grey--text mb-2">
                          <v-icon x-small class="mr-1">mdi-package-variant</v-icon>
                          Products ({{ secondChild.products.length }})
                        </div>
                        <div class="ml-2">
                          <v-chip
                              v-for="product in secondChild.products"
                              :key="product.id"
                              small
                              outlined
                              color="primary"
                              class="ma-1"
                              @click="edit_product(product, secondChild)"
                              style="cursor: pointer;"
                          >
                            <v-icon left x-small>mdi-package</v-icon>
                            {{ product.name }}
                          </v-chip>
                        </div>
                      </div>

                      <!-- Third Level Children -->
                      <div class="ml-8">
                        <div v-for="thirdChild in secondChild.children" :key="thirdChild.id" class="mb-2">
                          <div class="d-flex align-center mb-2">
                            <v-icon small color="grey" class="mr-2">mdi-subdirectory-arrow-right</v-icon>
                            <v-btn text class="pa-0 text-none" @click="edit_category(thirdChild)">
                              {{ thirdChild.name }}
                            </v-btn>
                          </div>

                          <!-- Products for Third Level -->
                          <div v-if="showProducts && thirdChild.products && thirdChild.products.length > 0" class="ml-6 mb-3">
                            <v-divider class="mb-2"></v-divider>
                            <div class="text-caption grey--text mb-2">
                              <v-icon x-small class="mr-1">mdi-package-variant</v-icon>
                              Products ({{ thirdChild.products.length }})
                            </div>
                            <div class="ml-2">
                              <v-chip
                                  v-for="product in thirdChild.products"
                                  :key="product.id"
                                  small
                                  outlined
                                  color="primary"
                                  class="ma-1"
                                  @click="edit_product(product, thirdChild)"
                                  style="cursor: pointer;"
                              >
                                <v-icon left x-small>mdi-package</v-icon>
                                {{ product.name }}
                              </v-chip>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
            </tbody>
          </template>
        </v-simple-table>
      </template>
    </v-card>

    <!-- Category Dialog -->
    <v-dialog v-model="category_dialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dark color="blue-grey darken-2">
          <v-btn icon dark @click="category_dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title class="white--text">{{ category.name || 'New Category' }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn dark text @click="save_category(category)">Save</v-btn>
        </v-toolbar>

        <v-container fluid>
          <v-row>
            <v-col cols="12" sm="8" offset-sm="2">
              <v-card flat>
                <v-card-text>
                  <v-form ref="form">
                    <v-text-field
                        v-model="category.name"
                        label="Category Name"
                        outlined
                        dense
                    ></v-text-field>
                  </v-form>

                  <template v-if="category.id">
                    <v-divider class="my-4"></v-divider>

                    <div class="d-flex justify-space-between align-center mb-4">
                      <div class="text-h6">Products</div>
                      <div>
                        <v-btn
                            color="blue-grey darken-2"
                            outlined
                            class="mr-2"
                            @click="copy_products_from_single(category)"
                        >
                          <v-icon left>mdi-content-copy</v-icon>
                          Copy Products
                        </v-btn>
                        <v-btn
                            color="blue-grey darken-2"
                            @click="edit_product({})"
                        >
                          <v-icon left>mdi-plus</v-icon>
                          Add Product
                        </v-btn>
                      </div>
                    </div>

                    <v-data-table
                        :headers="[
                        { text: 'Product', value: 'name' },
                        { text: 'Sizes', value: 'sizes', sortable: false },
                        { text: 'Mixes', value: 'mixes', sortable: false }
                      ]"
                        :items="category.products || []"
                        :loading="productsLoading"
                        class="elevation-1"
                    >
                      <template v-slot:item.name="{ item }">
                        <v-btn text class="pa-0" @click="edit_product(item, category)">{{ item.name }}</v-btn>
                      </template>

                      <template v-slot:item.sizes="{ item }">
                        <div class="d-flex align-center flex-wrap">
                          <v-chip
                              v-for="size in item.sizes"
                              :key="size.id"
                              small
                              class="mr-2 mb-1"
                              @click="edit_attr(size, item)"
                          >
                            {{ size.name }} - ${{ size.price }}
                          </v-chip>
                          <v-btn
                              icon
                              x-small
                              color="success"
                              @click="edit_attr({attribute:1}, item)"
                          >
                            <v-icon x-small>mdi-plus</v-icon>
                          </v-btn>
                        </div>
                      </template>

                      <template v-slot:item.mixes="{ item }">
                        <div class="d-flex align-center flex-wrap">
                          <v-chip
                              v-for="mix in item.mixes"
                              :key="mix.id"
                              small
                              class="mr-2 mb-1"
                              @click="edit_attr(mix, item)"
                          >
                            {{ mix.name }} - ${{ mix.price }}
                          </v-chip>
                          <v-btn
                              icon
                              x-small
                              color="success"
                              @click="edit_attr({attribute:2}, item)"
                          >
                            <v-icon x-small>mdi-plus</v-icon>
                          </v-btn>
                        </div>
                      </template>
                    </v-data-table>

                    <v-divider class="my-4"></v-divider>
                    <div class="d-flex justify-space-between align-center mb-4">
                      <div class="text-h6">Mandatory Options </div>
                      <div>
                        <v-btn
                            color="blue-grey darken-2"
                            @click="edit_option({})"
                        >
                          <v-icon left>mdi-plus</v-icon>
                          Add Option
                        </v-btn>
                      </div>
                    </div>

                    <v-data-table
                        :headers="[
                          { text: 'Product', value: 'product.name', width: '25%' },
                          { text: 'Sections', value: 'sectionSummary', width: '55%' },
                          { text: 'Actions', value: 'actions', sortable: false, width: '20%' }
                        ]"
                        :items="tableItems"
                        :loading="savingOption"
                        class="elevation-1"
                        item-key="id"
                    >
                      <template v-slot:item.sectionSummary="{ item }">
                        <div v-if="getSectionSummary(item).length > 0" class="pt-4 pb-4">
                          <div v-for="(section, index) in getSectionSummary(item)" :key="index" class="mb-2">
                            <div class="d-flex align-center">
                              <v-chip small color="primary" outlined class="mr-2">
                                Step {{ section.step }}
                              </v-chip>
                              <span class="font-weight-medium">{{ section.title }}</span>
                              <v-chip small color="grey lighten-1" class="ml-2" v-if="section.optionCount">
                                {{ section.optionCount }} option{{ section.optionCount !== 1 ? 's' : '' }}
                              </v-chip>
                            </div>
                          </div>
                        </div>
                        <span v-else class="grey--text">No sections defined</span>
                      </template>

                      <template v-slot:item.actions="{ item }">
                        <div class="d-flex" v-if="item">
                          <v-btn
                              icon
                              small
                              color="primary"
                              class="mr-1"
                              @click="editOptionFromTable(item)"
                          >
                            <v-icon small>mdi-pencil</v-icon>
                          </v-btn>
                          <v-btn
                              icon
                              small
                              color="error"
                              @click="delete_option(item)"
                          >
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </div>
                      </template>

                      <template v-slot:no-data>
                        <div class="text-center pa-4">
                          <v-icon large color="grey lighten-1">mdi-format-list-bulleted</v-icon>
                          <div class="grey--text mt-2">No mandatory options defined</div>
                        </div>
                      </template>
                    </v-data-table>
                  </template>
                </v-card-text>

                <v-divider v-if="category.id"></v-divider>

                <v-card-actions v-if="category.id">
                  <v-spacer></v-spacer>
                  <v-btn
                      color="error"
                      outlined
                      @click="delete_category(category)"
                  >
                    <v-icon left>mdi-delete</v-icon>
                    Delete Category
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Product Dialog -->
    <v-dialog v-model="product_dialog" max-width="500px">
      <v-card>
        <v-toolbar dense dark color="blue-grey darken-2">
          <v-toolbar-title class="white--text">{{ product.id ? 'Edit Product' : 'New Product' }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="product_dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <v-container>
            <v-form ref="productForm">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                      v-model="product.name"
                      label="Name"
                      dense
                      outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                      v-model="product.price"
                      label="Price"
                      type="number"
                      step="0.01"
                      prefix="$"
                      dense
                      outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-select
                      v-model="product.happy_hour_type"
                      :items="happy_hour_opts"
                      item-value="id"
                      item-text="name"
                      label="Happy Hour Type"
                      dense
                      outlined
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                      v-model="product.happy_hour_amount"
                      label="Happy Hour Amount"
                      type="number"
                      dense
                      outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                      v-model="product.tags"
                      label="Tags, separate by comma"
                      type="text"
                      dense
                      outlined
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn
              color="error"
              text
              @click="delete_product(product)"
              v-if="product.id"
          >
            Delete
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="product_dialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="save_product(product)">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Option Dialog -->
    <v-dialog v-model="option_dialog" max-width="500px" persistent>
      <v-card>
        <v-toolbar dense dark color="blue-grey darken-2">
          <v-toolbar-title class="white--text">{{ option.id ? 'Edit Option' : 'New Option' }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="option_dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <v-container>
            <v-form ref="optionForm">
              <v-row>
                <v-col cols="12">
                  <v-select
                      v-model="option.product"
                      :items="availableProducts"
                      item-value="id"
                      item-text="name"
                      label="Select Product"
                      :rules="[v => !!v || 'Product is required']"
                      outlined
                      return-object
                  ></v-select>

                  <v-text-field
                      v-model="numberOfSections"
                      label="Number of mandatory selections"
                      type="number"
                      min="1"
                      max="4"
                      :rules="[v => !!v || 'Required', v => (v > 0 && v <= 4) || 'Must be between 1 and 4']"
                      outlined
                      @change="updateSections"
                  ></v-text-field>

                  <v-alert v-if="numberOfSections > 0" type="info" text class="mb-4">
                    Please define each selection section below
                  </v-alert>

                  <div v-for="(section, index) in sections" :key="index" class="mb-6">
                    <v-card outlined class="pa-4">
                      <div class="d-flex align-center mb-4">
                        <div class="text-h6">Section {{ index + 1 }}</div>
                        <v-spacer></v-spacer>
                        <v-chip color="primary" small>Step {{ index }}</v-chip>
                      </div>

                      <v-text-field
                          v-model="section.title"
                          label="Section Title"
                          outlined
                          dense
                          :rules="[v => !!v || 'Title is required']"
                      ></v-text-field>

                      <div class="d-flex align-center mb-2">
                        <div class="subtitle-1 font-weight-medium">Options</div>
                        <v-spacer></v-spacer>
                        <v-btn small color="success" @click="addOption(index)">
                          <v-icon small left>mdi-plus</v-icon> Add Option
                        </v-btn>
                      </div>

                      <v-slide-y-transition group>
                        <div v-for="(sectionOption, optIndex) in section.options || []" :key="optIndex" class="mb-2">
                          <v-row>
                            <v-col cols="11">
                              <v-text-field
                                  v-model="sectionOption.name"
                                  label="Option Name"
                                  outlined
                                  dense
                                  :rules="[v => !!v || 'Option name is required']"
                              ></v-text-field>
                            </v-col>
                            <v-col cols="1" class="d-flex align-center">
                              <v-btn icon color="error" @click="removeOption(index, optIndex)">
                                <v-icon>mdi-delete</v-icon>
                              </v-btn>
                            </v-col>
                          </v-row>
                        </div>
                      </v-slide-y-transition>

                      <div v-if="!section.options || section.options.length === 0" class="text-center pa-3 grey--text">
                        No options added yet
                      </div>
                    </v-card>
                  </div>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn
              color="error"
              text
              @click="delete_option(option)"
              v-if="option.id"
          >
            Delete
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="option_dialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="save_option(sections)" :disabled="!option.product">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Attribute Dialog -->
    <v-dialog v-model="attr_dialog" max-width="500px">
      <v-card>
        <v-toolbar dense dark color="blue-grey darken-2">
          <v-toolbar-title>
            {{ attr.id ? 'Edit' : 'New' }} {{ attr.attribute === 1 ? 'Size' : 'Mix' }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="attr_dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                    v-model="attr.name"
                    :label="attr.attribute === 1 ? 'Size Name' : 'Mix Name'"
                    dense
                    outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                    v-model="attr.price"
                    label="Price"
                    type="number"
                    step="0.01"
                    prefix="$"
                    dense
                    outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-select
                    v-model="attr.happy_hour_type"
                    :items="happy_hour_opts"
                    item-value="id"
                    item-text="name"
                    label="Happy Hour Type"
                    dense
                    outlined
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-text-field
                    v-model="attr.happy_hour_amount"
                    label="Happy Hour Amount"
                    type="number"
                    dense
                    outlined
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn
              color="error"
              text
              @click="delete_attr(attr)"
              v-if="attr.id"
          >
            Delete
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="attr_dialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="save_attribute_value(attr, product)">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
        v-model="notification"
        :color="notification_type"
        :timeout="3000"
        top
    >
      {{ notification_message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="notification = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<style lang="scss">

</style>

<script>
import { debounce } from 'lodash'

export default {
  name: 'Categories',

  data() {
    return {
      // Loading states
      loading: false,
      saving: false,
      savingProduct: false,
      savingOption: false,
      savingAttr: false,
      copying: false,
      deleting: false,
      productsLoading: false,
      showProducts: false,

      // Data tables
      categories: [],
      headers: [
        { text: 'Name', value: 'name', sortable: false },
        { text: 'Sub Categories', value: 'children', sortable: false }
      ],
      productHeaders: [
        { text: 'Name', value: 'name' },
        { text: 'Price', value: 'price' },
        { text: 'Sizes', value: 'sizes', sortable: false },
        { text: 'Mixes', value: 'mixes', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false }
      ],

      // Dialogs
      category_dialog: false,
      product_dialog: false,
      option_dialog: false,
      attr_dialog: false,

      // Form data
      category: {
        id: 0,
        name: '',
        products: []
      },
      product: {
        id: 0,
        name: '',
        price: 0,
        happy_hour_amount: 0,
        happy_hour_type: 3,
        tags: '',
      },
      option: {
        id: 0,
        name: '',
        category: null,
      },
      attr: {
        id: 0,
        name: '',
        price: 0,
        happy_hour_amount: 0,
        happy_hour_type: 3
      },

      // Validation rules
      nameRules: [
        v => !!v || 'Name is required',
        v => v.length <= 500 || 'Name must be less than 500 characters'
      ],
      priceRules: [
        v => !!v || 'Price is required',
        v => v >= 0 || 'Price must be positive',
        v => /^\d*\.?\d{0,2}$/.test(v) || 'Price must have at most 2 decimal places'
      ],
      happyHourRules: [
        v => !!v || 'Amount is required',
        v => v >= 0 || 'Amount must be positive'
      ],

      // Options
      happy_hour_opts: [
        { id: 1, name: 'Different Price During HH' },
        { id: 2, name: 'Buy One Get One' },
        { id: 3, name: 'None' }
      ],

      // Notifications
      notification: false,
      notification_message: '',
      notification_type: 'success',

      numberOfSections: 0,
      sections: []
    }
  },

  computed: {
    availableProducts() {
      if (!this.category.products) return [];

      // If editing an existing option, include its product
      if (this.option.id && this.option.product) {
        return this.category.products.filter(product => {
          // Check if this product already has an option in tableItems
          const hasOption = this.tableItems.some(item =>
              item.product && item.product.id === product.id && item.id !== this.option.id
          );

          // Include if it doesn't have an option or it's the current option's product
          return !hasOption || product.id === this.option.product.id;
        });
      }

      // For new options, exclude products that already have options
      return this.category.products.filter(product => {
        return !this.tableItems.some(item =>
            item.product && item.product.id === product.id
        );
      });
    },
    dialogTitle() {
      return this.category.id ? `Edit ${this.category.name}` : 'New Category'
    },
    productDialogTitle() {
      return this.product.id ? `Edit ${this.product.name}` : 'New Product'
    },
    attrDialogTitle() {
      return this.attr.id ? `Edit ${this.attr.name}` : 'New ' + (this.attr.attribute === 1 ? 'Size' : 'Mix')
    },
    tableItems() {
      // If category.options is undefined or null, return empty array
      if (!this.category.options) return [];

      // If it's already an array, use it directly
      if (Array.isArray(this.category.options)) {
        return this.category.options.map(item => this.processOptionItem(item));
      }
      // If it's a single object (not in an array)
      else if (typeof this.category.options === 'object') {
        return [this.processOptionItem(this.category.options)];
      }

      // Fallback if nothing matches
      return [];
    },

  },

  created() {
    this.debouncedLoadCategories = debounce(this.load_categories, 300)
    this.load_categories()
  },

  methods: {
    processOptionItem(item) {
      if (!item) return { id: 0, options: [] };

      // Handle options data
      let optionsData = item.options || [];

      // Parse options if it's a string
      if (typeof optionsData === 'string') {
        try {
          optionsData = JSON.parse(optionsData);
        } catch (e) {
          console.error("Error parsing options JSON:", e);
          optionsData = [];
        }
      }

      // Ensure optionsData is an array
      if (!Array.isArray(optionsData)) {
        optionsData = [];
      }

      // Create a processed item with all needed properties
      const result = {
        id: item.id || 0,
        product: item.product || null,
        optionsData: optionsData, // Store the full options array for getSectionSummary
        options: [],
        title: '',
        step: 0
      };

      // Get the first option section if available
      if (optionsData.length > 0) {
        const firstOption = optionsData[0];
        result.options = firstOption.options || [];
        result.title = firstOption.title || '';
        result.step = firstOption.step !== undefined ? firstOption.step : 0;
      }

      return result;
    },
    // Add this method to your methods section
    getSectionSummary(item) {
      // If no item, return empty array
      if (!item) return [];

      // Get options data
      let optionsData = [];

      try {
        // If item has options property that's a string, parse it
        if (item.optionsData) {
          optionsData = item.optionsData;
        } else if (item.options && typeof item.options === 'string') {
          optionsData = JSON.parse(item.options);
        } else if (Array.isArray(item.options)) {
          // This might be the transformed data from tableItems
          return [{
            title: item.title || 'Untitled Section',
            step: item.step || 0,
            optionCount: Array.isArray(item.options) ? item.options.length : 0
          }];
        }
      } catch (e) {
        console.error("Error parsing options data in summary:", e);
        return [];
      }

      // If optionsData is not an array, return empty array
      if (!Array.isArray(optionsData)) return [];

      // Map options data to summary
      return optionsData.map((section, index) => {
        return {
          title: section.title || `Section ${index + 1}`,
          step: section.step !== undefined ? section.step : index,
          optionCount: Array.isArray(section.options) ? section.options.length : 0
        };
      });
    },
    // Add this new method to your methods section
    editOptionFromTable(tableItem) {
      console.log("Editing option from table item:", tableItem);

      // Find the original option from category.options using the ID
      let originalOption = null;

      if (Array.isArray(this.category.options)) {
        // If options is an array, find the matching item
        originalOption = this.category.options.find(opt => opt.id === tableItem.id);
      } else if (this.category.options && this.category.options.id === tableItem.id) {
        // If options is a single object
        originalOption = this.category.options;
      }

      console.log("Found original option:", originalOption);

      if (!originalOption) {
        console.error("Could not find original option with ID:", tableItem.id);
        this.showNotification('Error finding option data', 'error');
        return;
      }

      // Now call edit_option with the original option data
      this.edit_option(originalOption);
    },
    updateSections() {
      // Convert to number and ensure it's between 1-4
      this.numberOfSections = Math.min(4, Math.max(0, parseInt(this.numberOfSections) || 0));

      // If reducing sections, just trim the array
      if (this.sections.length > this.numberOfSections) {
        this.sections = this.sections.slice(0, this.numberOfSections);
      }
      // If adding sections, create new ones
      else if (this.sections.length < this.numberOfSections) {
        for (let i = this.sections.length; i < this.numberOfSections; i++) {
          this.sections.push({
            title: '',
            step: i,
            options: []
          });
        }
      }
    },

    addOption(sectionIndex) {
      this.sections[sectionIndex].options.push({
        name: ''
      });
    },

    removeOption(sectionIndex, optionIndex) {
      this.sections[sectionIndex].options.splice(optionIndex, 1);
    },
    formatPrice(price) {
      return `$${Number(price).toFixed(2)}`
    },

    showNotification(message, type = 'success') {
      this.notification = true
      this.notification_message = message
      this.notification_type = type === 'success' ? 'success' : 'error'
    },

    closeDialog() {
      this.category_dialog = false
      this.$nextTick(() => {
        this.category = {
          id: 0,
          name: '',
          products: []
        }
      })
    },

    async load_categories() {
      try {
        this.loading = true
        const response = await this.$http.get('/admin/categories/')
        this.categories = response.data

        // The API should return categories with their full nested structure including:
        // - children (subcategories)
        // - products array for each category
        // - sizes and mixes for each product
        // - options for each category
        console.log('Loaded categories:', response.data)
      } catch (error) {
        this.showNotification('Error loading categories', 'error')
        console.error('Error loading categories:', error)
      } finally {
        this.loading = false
      }
    },

    new_category(parent) {
      this.category = {
        parent: parent,
        name: '',
        id: 0,
        products: [],
        options: []
      }
      this.category_dialog = true
    },

    async save_category(category) {
      if (!category.name.trim()) {
        this.showNotification('Category name is required', 'error')
        return
      }

      try {
        this.saving = true
        if (category.id) {
          // Handle parent ID safely
          const parentId = category.parent && category.parent.id
              ? category.parent.id
              : category.parent

          await this.$http.patch(`/admin/categories/${category.id}/`, {
            ...category,
            parent: parentId
          })
        } else {
          await this.$http.post('/admin/categories/', category)
        }

        await this.load_categories()
        this.showNotification('Category saved successfully')
        this.closeDialog()
      } catch (error) {
        this.showNotification('Error saving category', 'error')
        console.error('Error saving category:', error)
      } finally {
        this.saving = false
      }
    },

    async delete_category(category) {
      if (!confirm(`Are you sure you want to delete ${category.name}? This will delete all sub-categories and products.`)) {
        return
      }

      try {
        this.deleting = true
        await this.$http.delete(`/admin/categories/${category.id}/`)
        await this.load_categories()
        this.showNotification('Category deleted successfully')
        this.closeDialog()
      } catch (error) {
        this.showNotification('Error deleting category', 'error')
        console.error('Error deleting category:', error)
      } finally {
        this.deleting = false
      }
    },

    edit_category(category) {
      // The category should already have products loaded from the initial API call
      this.category = { ...category }

      // Ensure products and options arrays exist even if empty
      if (!this.category.products) {
        this.category.products = []
      }
      if (!this.category.options) {
        this.category.options = []
      }

      this.category_dialog = true
    },

    async load_category_products(categoryId) {
      try {
        this.productsLoading = true
        const response = await this.$http.get(`/admin/categories/${categoryId}/`)

        // Update the category with the full data including products
        this.category = response.data

        // Also load options if they exist
        if (response.data.options) {
          this.category.options = response.data.options
        }

      } catch (error) {
        this.showNotification('Error loading category products', 'error')
        console.error('Error loading category products:', error)
      } finally {
        this.productsLoading = false
      }
    },

    async copy_products_from_single(category) {
      if (!confirm('Are you sure you want to copy products from other categories in the same sub tree?')) {
        return
      }

      try {
        this.copying = true
        await this.$http.post('/admin/category-copy-single/', {
          parent: category.parent_id,
          category: category.id
        })

        await this.load_categories()
        this.showNotification('Products copied successfully')
        this.closeDialog()
      } catch (error) {
        this.showNotification('Error copying products', 'error')
        console.error('Error copying products:', error)
      } finally {
        this.copying = false
      }
    },

    edit_product(product, category = null) {
      if (category) {
        this.category = category
      }

      this.product = product.id ? { ...product } : {
        id: 0,
        name: '',
        price: 0,
        happy_hour_amount: 0,
        happy_hour_type: 3,
        category: this.category.id,
        tags: ''
      }
      this.product_dialog = true
    },

    edit_option(option) {
      console.log("Edit option called with:", option);

      if (option && option.id) {
        // Editing existing option
        console.log("Editing existing option with ID:", option.id);

        // Create a clean copy of the option object
        this.option = {
          id: option.id,
          product: option.product,
          category: option.category || this.category.id
        };

        // Handle options data with extra safety
        let optionsData = [];

        try {
          // If we have options and it's a string, try to parse it
          if (option.options) {
            if (typeof option.options === 'string') {
              optionsData = JSON.parse(option.options);
              console.log("Parsed options data:", optionsData);
            }
            // If it's already an array, use it
            else if (Array.isArray(option.options)) {
              optionsData = [...option.options];
              console.log("Using array options data:", optionsData);
            }
          }
        } catch (e) {
          console.error("Error processing options data:", e);
          optionsData = [];
        }

        // Always ensure optionsData is an array
        if (!Array.isArray(optionsData)) {
          console.warn("optionsData is not an array, resetting to empty array");
          optionsData = [];
        }

        // Initialize sections with default structure if empty
        this.sections = optionsData.length > 0 ? optionsData : [{
          title: '',
          step: 0,
          options: []
        }];

        this.numberOfSections = this.sections.length;
        console.log("Sections initialized:", this.sections);

      } else {
        // Creating new option
        console.log("Creating new option");
        this.option = {
          id: 0,
          product: null,
          category: this.category.id
        };

        this.sections = [{
          title: '',
          step: 0,
          options: []
        }];

        this.numberOfSections = 1;
      }

      // Make sure we have at least one section
      if (this.sections.length === 0) {
        this.sections.push({
          title: '',
          step: 0,
          options: []
        });
        this.numberOfSections = 1;
      }

      // Ensure each section has an options array
      this.sections.forEach(section => {
        if (!section.options || !Array.isArray(section.options)) {
          section.options = [];
        }
      });

      this.option_dialog = true;
    },
    async save_product(product) {
      if (!this.$refs.productForm.validate()) {
        return
      }

      try {
        this.savingProduct = true
        if (product.id) {
          const response = await this.$http.patch(`/admin/products/${product.id}/`, product)
          const existingProduct = this.category.products.find(p => p.id === product.id);
          if (existingProduct) {
            Object.assign(existingProduct, response.data);
          }

        } else {
          const response = await this.$http.post('/admin/products/', product)
          this.category.products.push(response.data)
        }

        this.showNotification('Product saved successfully')
        this.product_dialog = false
      } catch (error) {
        this.showNotification('Error saving product', 'error')
        console.error('Error saving product:', error)
      } finally {
        this.savingProduct = false
      }
    },
    async save_option(sections) {
      if (!this.$refs.optionForm.validate()) {
        return;
      }

      if (!this.option.product) {
        this.showNotification('Please select a product', 'error');
        return;
      }

      const payload = {
        id: this.option.id,
        options: JSON.stringify(sections),
        category: this.category.id,
        product: this.option.product.id
      };

      try {
        this.savingOption = true;

        if (this.option.id) {
          // When updating, use the PATCH method
          await this.$http.patch(`/admin/options/${this.option.id}/`, payload);

          // Get the updated option with the proper serializer
          const singleOptionResponse = await this.$http.get(`/admin/options/${this.option.id}/`);

          // Update just this option in the category.options collection
          if (Array.isArray(this.category.options)) {
            // If options is an array, find and update
            const index = this.category.options.findIndex(opt => opt.id === this.option.id);
            if (index !== -1) {
              this.category.options.splice(index, 1, singleOptionResponse.data);
            } else {
              this.category.options.push(singleOptionResponse.data);
            }
          } else {
            // If options is a single object
            this.category.options = [singleOptionResponse.data];
          }
        } else {
          // Creating a new option
          const response = await this.$http.post('/admin/options/', payload);

          // Get the created option with the proper serializer
          const singleOptionResponse = await this.$http.get(`/admin/options/${response.data.id}/`);

          // Add to category.options
          if (Array.isArray(this.category.options)) {
            this.category.options.push(singleOptionResponse.data);
          } else if (this.category.options) {
            // If there's a single option, convert to array
            this.category.options = [this.category.options, singleOptionResponse.data];
          } else {
            // If no options yet
            this.category.options = [singleOptionResponse.data];
          }
        }

        this.showNotification('Options saved successfully');
        this.option_dialog = false;
      } catch (error) {
        this.showNotification('Error saving option', 'error');
        console.error('Error saving option:', error);
      } finally {
        this.savingOption = false;
      }
    },
    async delete_option(option) {
      if (!option || !option.id) {
        this.showNotification('Invalid option data', 'error');
        return;
      }

      if (!confirm('Are you sure you want to delete these options?')) {
        return;
      }

      try {
        this.savingOption = true;
        await this.$http.delete(`/admin/options/${option.id}/`);

        // Update local data structure instead of fetching all options again
        if (Array.isArray(this.category.options)) {
          // If options is an array, remove just the deleted option
          this.category.options = this.category.options.filter(opt => opt.id !== option.id);
        } else if (this.category.options && this.category.options.id === option.id) {
          // If options is a single object and it's the one being deleted
          this.category.options = [];
        }

        this.showNotification('Options deleted successfully');

        // If dialog is open, close it
        if (this.option_dialog) {
          this.option_dialog = false;
        }
      } catch (error) {
        this.showNotification('Error deleting option', 'error');
        console.error('Error deleting option:', error);
      } finally {
        this.savingOption = false;
      }
    },

    async delete_product(product) {
      if (!confirm(`Are you sure you want to delete ${product.name}?`)) {
        return
      }

      try {
        await this.$http.delete(`/admin/products/${product.id}/`)
        this.category.products = this.category.products.filter(p => p.id !== product.id)
        this.showNotification('Product deleted successfully')
        this.product_dialog = false
      } catch (error) {
        this.showNotification('Error deleting product', 'error')
        console.error('Error deleting product:', error)
      }
    },

    edit_attr(attr, product) {
      this.product = product
      this.attr = attr.id ? { ...attr } : {
        id: null,
        name: '',
        price: product.price,
        happy_hour_amount: product.happy_hour_amount,
        happy_hour_type: product.happy_hour_type,
        product: product.id,
        attribute: attr.attribute
      }
      this.attr_dialog = true
    },

    async save_attribute_value(attr, product) {
      if (!this.$refs.attrForm.validate()) {
        return
      }

      try {
        this.savingAttr = true
        if (attr.id) {
          await this.$http.patch(`/admin/attribute-values/${attr.id}/`, attr)
        } else {
          const response = await this.$http.post('/admin/attribute-values/', attr)
          if (attr.attribute === 2) {
            if (!product.mixes) product.mixes = []
            product.mixes.push(response.data)
          } else {
            if (!product.sizes) product.sizes = []
            product.sizes.push(response.data)
          }
        }

        this.showNotification('Attribute saved successfully')
        this.attr_dialog = false
      } catch (error) {
        this.showNotification('Error saving attribute', 'error')
        console.error('Error saving attribute:', error)
      } finally {
        this.savingAttr = false
      }
    },

    async delete_attr(attr) {
      if (!confirm(`Are you sure you want to delete this ${attr.attribute === 1 ? 'size' : 'mix'}?`)) {
        return
      }

      try {
        await this.$http.delete(`/admin/attribute-values/${attr.id}/`)
        if (attr.attribute === 2) {
          this.product.mixes = this.product.mixes.filter(m => m.id !== attr.id)
        } else {
          this.product.sizes = this.product.sizes.filter(s => s.id !== attr.id)
        }
        this.showNotification('Attribute deleted successfully')
        this.attr_dialog = false
      } catch (error) {
        this.showNotification('Error deleting attribute', 'error')
        console.error('Error deleting attribute:', error)
      }
    }
  }
}
</script>
